import {Pair} from '@packages/utilities/Pair';
import {
  DesignerWorkflow,
  DESIGNER_WORKFLOWS,
} from '@packages/systems/designer/workflows';
import {
  History,
  emptyHistory,
  getMostRecentHistoryItem,
  getUpcomingHistoryItem,
} from '@packages/utilities/undo-redo';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {SET_STYLE_ENDED} from '@packages/systems/designer/actions';
import {PaneUpdatedAction} from '@packages/systems/panels';

export {getMostRecentHistoryItem, getUpcomingHistoryItem};

export type UndoRedoStackType = 'designer' | 'logicFlows' | 'cms';

export type UndoableState = Pick<
  DesignerStores,
  | 'DesignerStore'
  | 'StyleBlockStore'
  | 'IX2UiStore'
  | 'IX3Store'
  | 'IXStore'
  | 'CssVariablesStore'
  | 'ValuesStore'
  | 'AssetStore'
  | 'ExternalStore'
  | 'StyleStore'
  | 'NavigatorStore'
  | 'IXSettingsStore'
  | 'CanvasResizeStore'
  | 'DomLocalizationStore'
  | 'LocalizationStore'
  | 'RequestStore'
  | 'MultiplayerStore'
  | 'ContentEditorStore'
  | 'CurrentCollectionStore'
  | 'UiNodeStore'
  | 'SourceFilesStore'
  | 'SiteDataStore'
>;

export type ReadableState = UndoableState & {
  RealtimeStore: {
    uuid: null | string;
    presences: DesignerStores['RealtimeStore']['presences'];
  };
  SiteDataStore: {
    shortName: string;
    isAISiteGen: boolean;
  };
  UiStore: {hasCompletedAISiteGenOnboarding?: boolean};
  SyncStore: {isEphemeralStyleChangeInProgress: boolean};
  EntitlementsStore: {
    entitlements: {
      allowOptimizeCore?: DesignerStores['EntitlementsStore']['entitlements']['allowOptimizeCore'];
    };
  };
  OptimizationStore: {
    currentVariationId: string | null;
  };
};

export type PastItem = {
  action: Action;
  undo: (
    currentState: UndoableState
  ) => Pair<UndoableState, (futureState: UndoableState) => UndoableState>;
};

export type FutureItem = {
  action: Action;
  undo: (
    currentState: UndoableState
  ) => Pair<UndoableState, (futureState: UndoableState) => UndoableState>;
  redo: (newState: UndoableState) => UndoableState;
};

export type HistoryItem = PastItem | FutureItem;

export type UndoRedoHistory = History<PastItem, FutureItem>;

export type UndoRedoStateType = {
  designer: UndoRedoHistory;
  logicFlows: UndoRedoHistory;
  cms: UndoRedoHistory;
};

export const getCurrentUndoRedoStack = (
  currentWorkflow: DesignerWorkflow,
  showInDataManagerModal: boolean | undefined
): UndoRedoStackType => {
  if (currentWorkflow === DESIGNER_WORKFLOWS.LOGIC_FLOWS) {
    return 'logicFlows';
  }

  // Outside Logic Flows, the Data Manager modal takes precedence over the workflow.
  if (
    currentWorkflow === DESIGNER_WORKFLOWS.DYNAMIC_DATA ||
    Boolean(showInDataManagerModal)
  ) {
    return 'cms';
  }

  return 'designer';
};

/**
 * The state slice needed to resolve the history the user is currently
 * operating on. Structural on purpose so flux store states, action states
 * and mapped props all satisfy it without extra imports.
 */
export type CurrentUndoRedoHistoryState = {
  DesignerStore: {workflow: DesignerWorkflow};
  CurrentCollectionStore?: {showInDataManagerModal?: boolean} | null;
  UndoRedoStore: UndoRedoStateType;
};

/** Resolves the current history from the workflow and Data Manager modal. */
export const getCurrentUndoRedoHistory = (
  state: CurrentUndoRedoHistoryState
): UndoRedoHistory => {
  const stack = getCurrentUndoRedoStack(
    state.DesignerStore.workflow,
    state.CurrentCollectionStore?.showInDataManagerModal
  );
  return state.UndoRedoStore[stack];
};

type SetStyleEndedAction = {
  type: typeof SET_STYLE_ENDED;
  payload: {
    commit: boolean;
    oldStates: {
      style: DesignerStores['StyleStore'];
      styleBlock: DesignerStores['StyleBlockStore'];
    };
  };
};

export type Action =
  | PaneUpdatedAction
  | SetStyleEndedAction
  | {
      type: string;
      payload: unknown;
    };

type UndoRedoFn = (
  stateAtUndo: UndoableState
) => Pair<UndoableState, (stateAtRedo: UndoableState) => UndoableState>;

export type UndoRedoHandler = (
  action: Action,
  prevState: ReadableState,
  nextState: UndoableState
) => null | UndoRedoFn;

export const initialState: UndoRedoStateType = {
  designer: emptyHistory(),
  logicFlows: emptyHistory(),
  cms: emptyHistory(),
};
